/* eslint-disable no-restricted-syntax */
import * as React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';

import moment from 'moment';

import { MOMENT_FORMATS, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Grid, RadioCard, RadioGroup, Typography2 } from '@coursera/cds-core';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import {
  PrivateSessionDrawerContext,
  getDateErrorMessage,
} from 'bundles/enterprise-admin-drawers/components/PrivateSessionEditDrawerContent';
import { DrawerInfo } from 'bundles/enterprise-admin-drawers/components/components/DrawerInfoBoxes';
import {
  CustomAvailabilityOption,
  ExternallyManagedOption,
  PrivateSessionGroupOption,
} from 'bundles/enterprise-admin-drawers/components/components/SessionOptions';
import type { CourseSession, CustomSessionOptionType } from 'bundles/enterprise-admin-drawers/components/types';
import { useProgramAdminTypeAndPermissionsContext } from 'bundles/enterprise-admin-program-catalog/components/ProgramAdminTypeAndPermissionsContextProvider';
import { assertIsExternallyManagedOption } from 'bundles/enterprise-admin-sessions/utils/typeHelpers';
import { TrackedButton } from 'bundles/enterprise-ui/components';
import type { DateRangeValue } from 'bundles/enterprise-ui/components/DateRangeInput';
import type {
  CreateSessionEligibility,
  EnterpriseAdminSessionSelectionOptionType,
  EnterpriseAdminSessionSelectionOptionTypeExternallyManagedSessionOption,
  EnterpriseAdminSessionSelectionOptionTypePrivateSessionGroupOption,
} from 'bundles/naptimejs/resources/__generated__/EnterpriseProductConfigurationsV1';
import type OnDemandCourseSessions from 'bundles/naptimejs/resources/onDemandCourseSessions.v1';

import _t from 'i18n!nls/admin-dashboard';

type Props = {
  selectableSessions: Array<EnterpriseAdminSessionSelectionOptionType | CustomSessionOptionType>;
  sessions: Array<OnDemandCourseSessions>;
  createSessionEligibility: CreateSessionEligibility;
  selectedSessionOption?: EnterpriseAdminSessionSelectionOptionType | CustomSessionOptionType;
  selectedSession?: CourseSession;
  dateError?: string | React.ReactNode;
  isOpen: boolean;
  isS12n?: boolean;
  isProfessionalCertificate?: boolean;
  onChangeSessionOption: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeDate: (date: DateRangeValue) => void;
  onChangeName: (e: React.ChangeEvent<HTMLInputElement>) => void;
  productId?: string;
  setDateError: (dateError: string | React.ReactNode | undefined) => void;
};

export const AvailabilityStep: React.FC<Props> = ({
  selectableSessions,
  sessions,
  createSessionEligibility,
  selectedSessionOption,
  selectedSession,
  dateError,
  isS12n,
  isProfessionalCertificate,
  onChangeSessionOption,
  onChangeDate,
  onChangeName,
  setDateError,
}) => {
  const hasSession =
    selectedSessionOption?.typeName === 'externallyManagedSessionOption' ||
    selectedSessionOption?.typeName === 'privateSessionGroupOption';
  const initialValue = hasSession
    ? `${selectedSessionOption?.typeName}~${
        (
          selectedSessionOption as
            | EnterpriseAdminSessionSelectionOptionTypeExternallyManagedSessionOption
            | EnterpriseAdminSessionSelectionOptionTypePrivateSessionGroupOption
        )?.definition.sessionId
      }`
    : `${selectedSessionOption?.typeName}`;
  const isCustom = selectedSessionOption?.typeName === 'createExternallyManagedSelection';
  useEffect(() => {
    const selectedRadio = document.querySelector(`input[value="${initialValue}"]`) as HTMLElement;
    selectedRadio?.focus();
  }, [initialValue]);

  const getHumanizedIneligibilityReason = (): string => {
    if (createSessionEligibility.typeName !== 'ineligible') return '';

    const reason = createSessionEligibility.definition.reason;
    const humanizedReasonMap = {
      NON_ENDED_SESSIONS_THRESHOLD_REACHED: _t(
        'You’ve reached the limit of 3 custom limited availabilities. You can add new custom limited availability when the current ones expire or you can edit the existing ones.'
      ),
      ENTERPRISE_PRIVATE_COURSE: _t(
        'New custom limited availabilities for this course must be setup in the Educator Admin. Please contact your organization’s administrator if the availability options above do not fulfill your requirements.'
      ),
      LIMITED_AVAILABILITY_COURSE: _t(
        'Custom limited availability is disabled for this course. The author of this course offers specific limited availability sessions throughout the year in order to offer the appropriate support to its learners.'
      ),
      CONTENT_EXPERIMENT_COURSE: _t('Custom limited availability is disabled for this course at this time.'),
      FEATURE_DISABLED: _t('Custom limited availability is disabled for this course at this time.'),
    };

    return humanizedReasonMap[reason];
  };

  const hasInitialValidationDone = useRef(false);

  const calculateMinimumDurationInDays = () => {
    if (assertIsExternallyManagedOption(selectedSessionOption)) {
      return selectedSessionOption.definition.minNumberOfDays;
    } else if (createSessionEligibility.typeName === 'eligible') {
      return createSessionEligibility.definition.minNumberOfDays;
    }

    return 0;
  };

  // Validates the initial session date range against minimum duration requirements and sets an error if needed
  const validateInitialDateRange = () => {
    if (hasInitialValidationDone.current) return;
    if (!selectedSession?.startsAt || !selectedSession?.endsAt) return;
    if (createSessionEligibility.typeName === 'ineligible') return;

    const minNumberOfDays = calculateMinimumDurationInDays();
    const durationInDays = moment(selectedSession.endsAt).diff(moment(selectedSession.startsAt), 'days');

    // Only set an error if the session duration is below the minimum required days
    if (durationInDays < minNumberOfDays) {
      setDateError(getDateErrorMessage(minNumberOfDays, isS12n, isProfessionalCertificate));
    }

    hasInitialValidationDone.current = true;
  };

  useEffect(() => {
    validateInitialDateRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSession?.startsAt, selectedSession?.endsAt]);

  const getAvailabilityHeader = () => {
    if (isS12n) {
      return isProfessionalCertificate
        ? _t('What is the availability for this Professional Certificate?')
        : _t('What is the availability for this Specialization?');
    } else {
      return _t('What is the availability for this course?');
    }
  };

  return (
    <RadioGroup
      value={initialValue}
      onChange={onChangeSessionOption}
      label={getAvailabilityHeader()}
      supportText={
        isS12n
          ? _t(
              'Custom limited availability must be longer than the duration of the courses and must end within 6 months of the date they are created'
            )
          : _t(
              'Custom limited availability must be longer than the duration of the course and must end within 6 months of the date they are created'
            )
      }
    >
      {selectableSessions.map((session) => {
        if (session.typeName === 'flexibleOption') {
          if (
            createSessionEligibility.typeName === 'ineligible' &&
            createSessionEligibility.definition.reason !== 'NON_ENDED_SESSIONS_THRESHOLD_REACHED'
          ) {
            return null;
          }
          return (
            <RadioCard
              label={_t('Always available')}
              value="flexibleOption"
              supportText={_t('Learners can start the course anytime and complete the course anytime')}
              data-testid="flexible-option-schedule-radio-button"
            />
          );
        }

        if (session.typeName === 'externallyManagedSessionOption') {
          let onDemandSession:
            | EnterpriseAdminSessionSelectionOptionTypeExternallyManagedSessionOption
            | CourseSession
            | undefined;
          let sessionName: string | undefined;

          const isSelected =
            (selectedSessionOption as EnterpriseAdminSessionSelectionOptionTypeExternallyManagedSessionOption)
              ?.definition.sessionId === session.definition.sessionId;

          if (isSelected) {
            onDemandSession = selectedSession;
            sessionName = selectedSession?.name !== undefined ? selectedSession?.name : session.definition.name;
          } else {
            onDemandSession = sessions.find((s) => s.id === session.definition.sessionId);
            sessionName = session.definition.name;
          }

          if (!onDemandSession) {
            return null;
          }

          return (
            <ExternallyManagedOption
              key={`externallyManagedSessionOption~${session.definition.sessionId}`}
              isSelected={isSelected}
              sessionId={session.definition.sessionId}
              startsAt={onDemandSession.startsAt || 0}
              endsAt={onDemandSession.endsAt || 0}
              name={sessionName}
              canEdit={session.definition.canEdit}
              maxEndDate={session.definition.maxEndDate}
              onChangeDate={onChangeDate}
              onChangeName={onChangeName}
              error={dateError}
            />
          );
        }

        if (session.typeName === 'privateSessionGroupOption') {
          const onDemandSession = sessions.find((s) => s.id === session.definition.sessionId);

          if (!onDemandSession) {
            return null;
          }

          return (
            <PrivateSessionGroupOption
              key={`privateSessionGroupOption~${session.definition.sessionId}`}
              sessionId={session.definition.sessionId}
              startsAt={onDemandSession.startsAt}
              endsAt={onDemandSession.endsAt}
              name={session.definition.groupName}
            />
          );
        }

        return null;
      })}
      {createSessionEligibility.typeName !== 'ineligible' ? (
        <CustomAvailabilityOption
          onRadioSelected={onChangeSessionOption}
          showCustomDateInputs={isCustom}
          startsAt={selectedSession?.startsAt || 0}
          endsAt={selectedSession?.endsAt || 0}
          name={selectedSession?.name}
          onChangeDate={onChangeDate}
          onChangeName={onChangeName}
          error={dateError}
          maxEndDate={createSessionEligibility.definition.maxEndDate}
        />
      ) : (
        <DrawerInfo theme="blue" text={getHumanizedIneligibilityReason()} />
      )}
    </RadioGroup>
  );
};

export const AvailabilityStepCollapsed: React.FC<Props & { openStep: () => void }> = ({
  selectedSessionOption,
  selectedSession,
  openStep,
  isS12n,
  isProfessionalCertificate,
  productId,
  createSessionEligibility,
}) => {
  const isFlexible = selectedSessionOption?.typeName === 'flexibleOption';
  const { programId, orgId, userId } = useProgramAdminTypeAndPermissionsContext();

  const getAvailabilityText = () => {
    if (isS12n && isFlexible) {
      return isProfessionalCertificate
        ? _t('This Professional Certificate is always available')
        : _t('This Specialization is always available');
    } else if (isFlexible) {
      if (
        createSessionEligibility.typeName === 'ineligible' &&
        createSessionEligibility.definition.reason !== 'NON_ENDED_SESSIONS_THRESHOLD_REACHED'
      ) {
        return _t('This course is disabled');
      }
      return _t('This course is always available');
    } else if (isS12n) {
      return isProfessionalCertificate
        ? _t('This Professional Certificate is being offered on a custom limited availability')
        : _t('This Specialization is being offered on a custom limited availability');
    } else {
      return _t('This course is being offered on a custom limited availability');
    }
  };

  return (
    <Grid container spacing={16} direction="column">
      <Grid item>
        <Typography2 variant="subtitleMedium" component="h4">
          {getAvailabilityText()}
        </Typography2>
        {isFlexible ? (
          (createSessionEligibility.typeName !== 'ineligible' ||
            createSessionEligibility.definition.reason === 'NON_ENDED_SESSIONS_THRESHOLD_REACHED') && (
            <Typography2 component="p" variant="bodySecondary">
              {isS12n
                ? _t('Learners can start courses anytime and complete courses anytime')
                : _t('Learners can start the course anytime and complete the course anytime')}
            </Typography2>
          )
        ) : (
          <Grid container direction="row" justifyContent="space-between">
            {selectedSession?.startsAt && (
              <Grid item>
                <Typography2 component="p">{_t('Start Date')}</Typography2>
                <Typography2 component="p" color="supportText">
                  {formatDateTimeDisplay(selectedSession.startsAt, MOMENT_FORMATS.LONG_DATE_ONLY_DISPLAY)}
                </Typography2>
              </Grid>
            )}
            {selectedSession?.endsAt && (
              <Grid item>
                <Typography2 component="p">{_t('End Date')}</Typography2>
                <Typography2 component="p" color="supportText">
                  {formatDateTimeDisplay(selectedSession.endsAt, MOMENT_FORMATS.LONG_DATE_ONLY_DISPLAY)}
                </Typography2>
              </Grid>
            )}
            {selectedSession?.name ? (
              <Grid item>
                <Typography2 component="p">{_t('Availability Name')}</Typography2>
                <Typography2 component="p" color="supportText">
                  {selectedSession.name}
                </Typography2>
              </Grid>
            ) : (
              <div />
            )}
          </Grid>
        )}
      </Grid>
      <Grid item>
        <TrackedButton
          trackingName="enterprise_admin_edit_availability_btn"
          trackingData={{
            userId,
            programId,
            orgId,
            productId,
          }}
          variant="secondary"
          onClick={openStep}
          data-testid="show-edit-availability"
          size="small"
        >
          {_t('Edit')}
        </TrackedButton>
      </Grid>
    </Grid>
  );
};

const AvailabilityStepWrapped: React.FC<Props> = (props) => {
  const [isCollapsed, setIsCollapsed] = useState(!props.isOpen);
  const { isS12n, isProfessionalCertificate } = useContext(PrivateSessionDrawerContext);

  return isCollapsed ? (
    <AvailabilityStepCollapsed
      {...props}
      openStep={() => setIsCollapsed(false)}
      isS12n={isS12n}
      isProfessionalCertificate={isProfessionalCertificate}
    />
  ) : (
    <AvailabilityStep {...props} isS12n={isS12n} isProfessionalCertificate={isProfessionalCertificate} />
  );
};

export default AvailabilityStepWrapped;
